


































































import Vue from "vue";
import Component from "vue-class-component";
import { Emit, Prop, Watch } from "vue-property-decorator";
import { DateHelper } from "../../core/helpers/dateHelper";
import { Format } from "../../core/helpers/formatHelper";
import store from "../../core/store";

@Component({})
export default class DateStripPicker extends Vue {
  @Prop({ default: "" }) value!: string;
  @Prop({ default: null }) defaultDate!: Date | null;
  @Prop({ default: 5 }) range!: number;
  @Prop({ default: null }) minDate!: Date | null;
  @Prop({ default: null }) maxDate!: Date | null;
  @Prop({ default: "" }) locale!: string;

  currentDateStr: string = "";
  screenWidth: number =
    typeof window === "undefined" ? 1024 : window.innerWidth;
  resizeHandler: (() => void) | null = null;

  mounted() {
    this.syncFromProps(false);
    this.attachResizeListener();
  }

  beforeDestroy() {
    this.detachResizeListener();
  }

  @Watch("value")
  onValueChange(newVal: string) {
    if (!Format.IsNull(newVal)) {
      this.currentDateStr = newVal;
      this.scrollActiveDateIntoView();
      return;
    }
    if (this.defaultDate) {
      this.setDateFromDate(this.defaultDate, false);
      return;
    }
    this.currentDateStr = DateHelper.toSystemString(store.getters.localNow);
    this.scrollActiveDateIntoView();
  }

  @Watch("defaultDate")
  onDefaultDateChange(newVal: Date | null) {
    if (newVal) {
      this.setDateFromDate(newVal, Format.IsNull(this.value));
    }
  }

  get userLocale() {
    if (this.locale) {
      return this.locale;
    }
    const stored = localStorage.getItem(window.location.host + "_culture");
    return stored || "es-ES";
  }

  get dateStripDays() {
    const base = this.getSelectedDate();
    if (!base) return [];
    const days: any[] = [];
    const range = this.getEffectiveRange();
    for (let i = -range; i <= range; i++) {
      const date = DateHelper.addDays(new Date(base), i);
      if (!date) continue;
      days.push({
        key: DateHelper.toSystemString(date),
        date,
        day: this.formatDay(date),
        month: this.formatMonth(date),
        weekday: this.formatWeekday(date),
        isActive: this.isSameDay(base, date),
        isDisabled: !this.isDateAllowed(date),
      });
    }
    return days;
  }

  syncFromProps(emitOnDefault: boolean) {
    if (!Format.IsNull(this.value)) {
      this.currentDateStr = this.value;
      this.scrollActiveDateIntoView();
      return;
    }
    if (this.defaultDate) {
      this.setDateFromDate(this.defaultDate, emitOnDefault);
      return;
    }
    const now = store.getters.localNow;
    this.currentDateStr = DateHelper.toSystemString(this.toDateOnly(now));
    this.scrollActiveDateIntoView();
  }

  getEffectiveRange() {
    if (this.screenWidth <= 767) {
      return Math.min(this.range, 3);
    }
    return this.range;
  }

  getSelectedDate() {
    if (!this.currentDateStr) return store.getters.localNow;
    const parsed = DateHelper.toSystemDate(this.currentDateStr);
    return parsed || store.getters.localNow;
  }

  formatMonth(date: Date) {
    const value = date.toLocaleDateString(this.userLocale, { month: "short" });
    return this.cleanShortLabel(value);
  }

  formatWeekday(date: Date) {
    const value = date.toLocaleDateString(this.userLocale, {
      weekday: "short",
    });
    return this.cleanShortLabel(value);
  }

  formatDay(date: Date) {
    return date
      .getDate()
      .toString()
      .padStart(2, "0");
  }

  cleanShortLabel(value: string) {
    if (!value) return "";
    const cleaned = value.replace(".", "");
    return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
  }

  isSameDay(a: Date, b: Date) {
    return (
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate()
    );
  }

  isDateAllowed(date: Date) {
    const min = this.getMinDate();
    if (min && this.toDateOnly(date).getTime() < min.getTime()) {
      return false;
    }
    const max = this.getMaxDate();
    if (max && this.toDateOnly(date).getTime() > max.getTime()) {
      return false;
    }
    return true;
  }

  getMinDate() {
    if (this.minDate) {
      return this.toDateOnly(this.minDate);
    }
    if (this.maxDate) {
      return this.toDateOnly(store.getters.localNow);
    }
    return null;
  }

  getMaxDate() {
    if (!this.maxDate) return null;
    return this.toDateOnly(this.maxDate);
  }

  toDateOnly(date: Date) {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
  }

  setDateFromDate(date: Date, emitChange: boolean) {
    if (!date) return;
    const normalized = this.toDateOnly(date);
    this.currentDateStr = DateHelper.toSystemString(normalized);
    this.scrollActiveDateIntoView();
    if (emitChange) {
      this.emitDateChanged(normalized);
    }
  }

  selectDate(date: Date) {
    if (!this.isDateAllowed(date)) return;
    this.setDateFromDate(date, true);
  }

  canShift(delta: number) {
    const base = this.getSelectedDate();
    if (!base) return false;
    const next = DateHelper.addDays(new Date(base), delta);
    if (!next) return false;
    return this.isDateAllowed(next);
  }

  shiftDate(delta: number) {
    if (!this.canShift(delta)) return;
    const base = this.getSelectedDate();
    if (!base) return;
    const next = DateHelper.addDays(new Date(base), delta);
    if (!next) return;
    this.selectDate(next);
  }

  scrollActiveDateIntoView() {
    this.$nextTick(() => {
      const strip = this.$refs.dateStrip as HTMLElement | undefined;
      if (!strip) return;
      const active = strip.querySelector(
        ".classesPage__dateItem.is-active",
      ) as HTMLElement | null;
      if (!active) return;
      active.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    });
  }

  attachResizeListener() {
    if (typeof window === "undefined") return;
    this.updateScreenWidth();
    this.resizeHandler = () => this.updateScreenWidth();
    window.addEventListener("resize", this.resizeHandler);
  }

  detachResizeListener() {
    if (!this.resizeHandler) return;
    if (typeof window !== "undefined") {
      window.removeEventListener("resize", this.resizeHandler);
    }
    this.resizeHandler = null;
  }

  updateScreenWidth() {
    if (typeof window === "undefined") return;
    this.screenWidth = window.innerWidth;
  }

  @Emit("dateChanged")
  emitDateChanged(date: Date) {
    return {
      date,
      systemStrDate: DateHelper.toSystemString(date),
    };
  }
}
